import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Product, ProductScope, ProductService, RoutingService, VariantOption, isNotUndefined } from '@spartacus/core';
import { MediaComponent } from '@spartacus/storefront';
import { uniqBy } from 'lodash';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { PlpPopupService } from 'src/app/service/plp-popup/plp-popup.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { RemoveImageTitleAttrDirective } from '../../../../../shared/remove-title-attr.directive';

@Component({
  selector: 'app-variant-size-selector',
  templateUrl: './variant-size-selector.component.html',
  styleUrls: ['./variant-size-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgClass, NgFor, MediaComponent, RemoveImageTitleAttrDirective, MatTooltip],
})
export class VariantSizeSelectorComponent implements AfterViewChecked, OnDestroy, OnChanges {
  @ViewChild('hasVariantList', { static: false }) private hasVariantList: ElementRef<HTMLInputElement>;
  businessType: string;
  variantColorConfigSubscription$: Subscription;
  vcLargeThumbnailEnabled: boolean;

  notAavailableVvc: any;
  availableVvc: any;
  availableVvcUnique: any;
  notAavailableVvcUnique: any;
  finalVariants: any;
  pdpAllVvcEnabled: Subscription;
  isPdpAllVvcEnable: boolean;

  constructor(
    private productService: ProductService,
    private customProductService: CustomProductService,
    private routingService: RoutingService,
    private cdr: ChangeDetectorRef,
    private currentBusinesstype: CurrentBusinessType,
    private renderer: Renderer2,
    private pdpGetAllVvcService?: PlpPopupService
  ) {
    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  @Input()
  product: Product;

  @Input()
  variants: any;

  @Input()
  title: string;

  @Input()
  isDesktop: boolean;

  @Input()
  selectedVariantIndex: number;

  @Input()
  firstSelector: boolean;

  @Output() parentUpdate: EventEmitter<any> = new EventEmitter();

  @Output() isLoading: EventEmitter<any> = new EventEmitter();

  @Output() parentReload: EventEmitter<any> = new EventEmitter();

  isColor = false;

  changeSize(code: string, selected: boolean): void {
    if (code && !selected) {
      const variantSectionHeight = document.getElementById('prod-variant-section').offsetHeight + 'px';
      document.getElementById('prod-variant-section').style.minHeight = variantSectionHeight;
      this.isLoading.emit();
      this.productService
        .get(code, ProductScope.DETAILS)
        .pipe(
          // below call might looks redundant but in fact this data is going to be loaded anyways
          // we're just calling it earlier and storing
          filter(isNotUndefined),
          take(1)
        )
        .subscribe(
          (product: Product) => {
            this.routingService.go({
              cxRoute: 'product',
              params: product,
            });
          },
          (err) => {
            this.parentUpdate.emit(err);
          }
        );
    }
    return null;
  }

  ngOnInit(): void {
    this.isVariantColorConfigEnabled();
    this.isPdpAllVvcEnabled();
  }

  combinedAllVVC() {
    this.notAavailableVvc = [];
    this.notAavailableVvcUnique = [];
    this.availableVvc = [];
    this.variants.forEach((vvc: any) => {
      const vvcObj = vvc;
      if (vvc.prevCategory.length > 0) {
        const category = vvc.categorySelected;
        let notavailable = false;
        vvc.prevCategory.forEach((cat: any, index: any) => {
          if (category[index] !== cat) {
            notavailable = true;
          }
        });
        if (notavailable) {
          this.notAavailableVvc.push({ ...vvcObj, available: false });
        } else {
          this.availableVvc.push({ ...vvcObj, available: true });
        }
      } else {
        // For Parent or First Element behavior
        const categorySelected = vvc.categorySelected;
        const vvcName = vvc.name;

        let hasAvailableVVC = true;
        let outOfStock = false;
        let getIndex = [];
        let parentIndex;
        if (categorySelected.length >= 0) {
          for (const [index, item] of categorySelected.entries()) {
            if (index >= 1) {
              const vvcSelected = item; // XXL
              let baseOptionVariant: VariantOption;

              const parentElement = this.product.variantMatrix.find(
                (item) => item.variantValueCategory.name === vvcName
              );
              if (parentElement) {
                let childElement = parentElement.elements.find(
                  (variant) => variant.variantValueCategory.name === vvcSelected
                )?.variantOption.code;
                if (childElement) {
                  baseOptionVariant = this.product.baseOptions[0].options.find((item) => item.code === childElement);
                  if (baseOptionVariant?.stock?.stockLevelStatus == 'outOfStock') {
                    outOfStock = true;
                  } else {
                    outOfStock = false;
                  }
                }
              }

              if (parentElement) {
                if (index === 1) {
                  const parentIndex = parentElement.elements.findIndex(
                    (item) => item.variantValueCategory.name === vvcSelected
                  );

                  getIndex.push(parentIndex);
                  if (parentIndex === -1) {
                    hasAvailableVVC = false;
                    break;
                  }
                }
                if (index > 1) {
                  const prevIndex = this.accessNestedValue(parentElement, getIndex);

                  const childIndex = prevIndex.elements.findIndex(
                    (item: any) => item.variantValueCategory.name === vvcSelected
                  );

                  getIndex.push(childIndex);
                  if (childIndex === -1) {
                    hasAvailableVVC = false;
                    break;
                  }
                }
              }
            }
          }
        }
        if (hasAvailableVVC) {
          this.availableVvc.push({ ...vvcObj, available: true, hasOutStockVvc: outOfStock });
        } else {
          this.availableVvc.push({ ...vvcObj, available: false, hasOutStockVvc: outOfStock });
        }
      }
    });
    this.availableVvcUnique = uniqBy(this.availableVvc, 'name');
    this.notAavailableVvcUnique = uniqBy(this.notAavailableVvc, 'name');
    let finalNotAvailable = this.notAavailableVvcUnique.filter(
      (notavail: any) => !this.availableVvcUnique.some((avail: any) => avail.name === notavail.name)
    );
    if (finalNotAvailable.length) {
      this.finalVariants = [...this.availableVvcUnique, ...finalNotAvailable];
    } else {
      this.finalVariants = this.availableVvcUnique;
    }
    this.finalVariants.sort((firstItem: any, secondItem: any) => firstItem.sequence - secondItem.sequence); //sort by sequence property
    this.cdr.detectChanges();
  }

  accessNestedValue(parent: any, indices: any) {
    let currentElement = parent;

    for (let i = 0; i < indices.length; i++) {
      if (currentElement.elements && Array.isArray(currentElement.elements)) {
        const index = indices[i];

        if (currentElement.elements[index]) {
          currentElement = currentElement.elements[index]; // next nested level
        } else {
          return null;
        }
      } else {
        return null;
      }
    }

    return currentElement; // Return the final element
  }

  ngAfterViewChecked() {
    if (this.hasVariantList) {
      const hasLI = this.hasVariantList.nativeElement.querySelector('li');
      if (hasLI === null) {
        this.hasVariantList.nativeElement.querySelector('.variant-name').classList.add('d-none');
      } else {
        this.hasVariantList.nativeElement.querySelector('.variant-name').classList.remove('d-none');
      }
    }
    this.cdr.detectChanges();
  }

  ngOnChanges({ variants }: SimpleChanges) {
    if (
      (variants?.previousValue !== variants?.currentValue || variants?.previousValue === undefined) &&
      !variants?.firstChange
    ) {
      this.combinedAllVVC();
    }
  }

  showProductSelectedVariant(category: any, prevSelected: any) {
    let showVariantCategory = true;
    prevSelected.forEach((cat: any, index: any) => {
      if (category[index] !== cat) {
        showVariantCategory = false;
      }
    });
    return showVariantCategory;
  }

  isVariantColorConfigEnabled(): void {
    this.variantColorConfigSubscription$ = this.customProductService.getVariantColorConfig$().subscribe({
      next: (enabled) => {
        this.vcLargeThumbnailEnabled = enabled === 'true';

        if (this.largeThumbnailEnabled()) {
          this.adjustElementsHeight();
        }
        this.cdr.detectChanges();
      },
    });
  }

  getImageRendition(image: any) {
    let size: string;
    size = ShimanoUtilMethod.imageRendition(this.vcLargeThumbnailEnabled && this.businessType === 'fish' ? 98 : 40);
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  largeThumbnailEnabled(): boolean {
    return (
      this.vcLargeThumbnailEnabled && this.businessType === 'fish' && (this.title == 'Colour' || this.title == 'Color')
    );
  }
  isColors(variants: any) {
    if (variants?.length > 0) {
      if (variants[0]?.hasImage) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  adjustElementsHeight(): void {
    const productReplacementEl = document.querySelector('app-product-replacement');
    this.renderer.setStyle(productReplacementEl, 'min-height', '32px');
  }

  isPdpAllVvcEnabled = () => {
    this.pdpAllVvcEnabled = this.pdpGetAllVvcService.getPdpAllVvcEnabledConfigs$().subscribe((pdpVvcAll) => {
      this.isPdpAllVvcEnable = pdpVvcAll.toString() === 'true';
      if (this.isPdpAllVvcEnable) {
        this.combinedAllVVC();
      }
      this.cdr.detectChanges();
    });
  };

  ngOnDestroy(): void {
    this.variantColorConfigSubscription$?.unsubscribe();

    if (this.pdpAllVvcEnabled) {
      this.pdpAllVvcEnabled.unsubscribe();
    }
  }
}
